import { z } from "zod";
import { InvitableInternalRole, ParticipantRole } from "@/enum/common.enum";
import { DEFAULT_COUNTRY, isValidPhone } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";

export const orgInviteSchema = z.object({
  email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
  role: z.nativeEnum(InvitableInternalRole),
});

export const dealInviteSchema = z
  .object({
    email: z.string().trim().min(1, "Email is required.").email("Enter a valid email address."),
    deal_id: z.string().trim().min(1, "Select a deal."),
    participant_role: z.nativeEnum(ParticipantRole),
    name: z.string().trim().optional(),
    // Email stays required even when texting: it is the invitation's identity
    // key, not just a delivery address. The phone is a second channel for the
    // same link.
    phone: z.string().trim().optional(),
    /** ISO 3166-1 alpha-2 the phone is read against. Never sent to the server —
     *  it exists to turn `phone` into E.164 before the request is built.
     *  Required rather than `.default()`, which would make the schema's input
     *  type diverge from its output and break a typed resolver. */
    phone_country: z.string().trim().min(2),
    send_sms: z.boolean().optional(),
  })
  // Validated here, not only server-side. `libphonenumber-js` is built from the
  // same metadata as the backend's `phonenumbers`, so this rejects exactly what
  // `normalize_phone` would have rejected — except it does so while the agent
  // is still looking at the field, instead of after the invitation exists and
  // the SMS has already come back unsendable.
  .superRefine((values, ctx) => {
    if (!values.send_sms) return;
    if (!values.phone) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["phone"], message: "Add a mobile number to send a text." });
      return;
    }
    if (!isValidPhone(values.phone, (values.phone_country || DEFAULT_COUNTRY) as CountryCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phone"],
        message: "That isn't a valid number for the selected country. Check the country code and try again.",
      });
    }
  });

export type OrgInviteValues = z.infer<typeof orgInviteSchema>;
export type DealInviteValues = z.infer<typeof dealInviteSchema>;
